import { Empty } from "antd";
import { UrlInternal } from "common/constants/endpoints";
import useWindowSize, { Size } from "hooks/useWindowSize";
import moment from "moment";
import React, { useEffect, useRef, useState } from "react"; //lazy
import * as S from "./style";
import moneyConvertLang from "utils/moneyConvertLang";
import { Swiper, SwiperSlide } from "swiper/react";
import { Pagination, Navigation, Autoplay } from "swiper";
import "swiper/swiper-bundle.css";
import SliderButton from "assets/images/banner/slider_button.svg";
import SliderPrevButton from "assets/images/banner/slider_prev_button.svg";
import Countdown from "react-countdown";
import { ImagePopup, ProgressBar } from "components";
import ModalDefault from "components/ModalDefault";

interface Props {
  data?: any;
  target?: string;
  isMobile?: boolean;
  onFetchData?: () => void;
}

interface SliderProps {
  id: number;
  startDate: string;
  endDate: string;
  target: number;
  artworkName: string;
  currentMoney: number;
  unitPrice: number;
  maxUnitBuyPerUser: number;
  status: string;
  yearOfItem: string;
  artMaterial: string;
  artSize: string;
  subCategory: string;
  eventType: string | null;
  eventEndDate: string | null;
  gpVer: string;
  startRegRandAt: string;
  endRegRandAt: string;
  endRandAt: string;
  unitForRand: number;
  endNormalBuyAt: string;
  cpCodeV3: string;
  expectedTotalUnitForUserV3: number;
  title: string;
  authorName: string;
  v3TotalUnitReg: number;
  images: string[];
  thumbnail: string;
  targetPercent: number;
  stock: number;
}

interface SwiperInstance {
  activeIndex: number;
  slides: { length: number };
  slideNext: () => void;
  slidePrev: () => void;
}

const toKstMoment = (date: string) => {
  return moment(date, "YYYY-MM-DD HH:mm:ss").tz("Asia/Seoul");
};

const MainSlider = ({ data = [], target = "", onFetchData }: Props) => {
  const [swiperReady, setSwiperReady] = useState(false);
  const size: Size = useWindowSize();
  const isMobile = typeof size.width === "number" && size.width <= 767;
  const [sliderData, setSliderData] = useState<SliderProps[]>([]);
  const [isPopup, setIsPopup] = useState<string>("");
  const now = moment(moment().toISOString(), "YYYY-MM-DD HH:mm:ss").tz(
    "Asia/Seoul"
  );
  const [index, setIndex] = useState<number>(0);
  const swiperRef = useRef<SwiperInstance | null>(null);
  const imageRefs = useRef<(HTMLImageElement | null)[]>([]);
  const [is80Value, setIs80Value] = useState<boolean>(false);

  useEffect(() => {
    fetchData();
    setTimeout(() => {
      const currentImage = imageRefs.current?.[0];
      if (currentImage) {
        currentImage.style.transition = "transform 0.3s ease-in-out";
        currentImage.style.transform = "scale(1.05)";
        setTimeout(() => {
          currentImage.style.transform = "scale(1)";
        }, 300);
      }
    }, 500);
  }, [isMobile]);

  const fetchData = () => {
    const result = renderDataSlider();
    setSliderData(result);
    setIs80Value(
      result.some(
        (item: SliderProps) =>
          Math.round(
            ((item.v3TotalUnitReg * item.unitPrice) / (item.target * 0.9)) * 100
          ) > 80
      )
    );
  };

  const settings = {
    modules: [Navigation, Autoplay],
    autoplay: { delay: 5000, disableOnInteraction: false },
    centeredSlides: true,
    navigation: {
      nextEl: ".next",
      prevEl: ".prev",
    },
    simulateTouch: false,
    allowTouchMove: false,
  };

  const mobileSettings = {
    modules: [Pagination],
    pagination: {
      el: ".swiper-pagination",
      clickable: true,
    },
    autoplay: false,
    centeredSlides: true,
    autoHeight: true,
    spaceBetween: 15,
  };

  const handleCheckTimeEndEvent = (endDate: string) => {
    return now.isBefore(toKstMoment(endDate));
  };

  const isCountDown = (date: string) => {
    const target = toKstMoment(date);
    const oneDayBefore = moment(target).subtract(1, "days");

    return now.isSameOrAfter(oneDayBefore) && now.isBefore(target);
  };

  const isCountDownForStartDate = (date: string) => {
    return now.isBefore(toKstMoment(date));
  };

  const prefixO = (dataNumber: number) => {
    return dataNumber < 10 ? `0${dataNumber}` : dataNumber;
  };

  const rendererIsComming = ({
    hours,
    minutes,
    seconds,
    completed,
    days,
    props,
  }: any) => {
    if (!completed) {
      const targetDate = new Date(props.date);
      const koreaTime = moment(targetDate).tz("Asia/Seoul");

      return (
        <div>
          {days > 0 ? (
            `${koreaTime.format("YYYY.MM.DD hh:mm A")} OPEN`
          ) : (
            <span className="time-event">
              {prefixO(hours)} : {prefixO(minutes)} : {prefixO(seconds)}
            </span>
          )}
        </div>
      );
    }
  };

  const rendererIsEnding = ({ hours, minutes, seconds, completed }: any) => {
    if (!completed) {
      return (
        <div>
          <span className="time-event">
            {prefixO(hours)} : {prefixO(minutes)} : {prefixO(seconds)}
          </span>
        </div>
      );
    }
  };

  const handleShowCountDownIsComming = (startDate?: string) => {
    return (
      <Countdown
        date={new Date(startDate ?? new Date()).getTime()}
        renderer={rendererIsComming}
        onComplete={onFetchData}
      />
    );
  };

  const handleShowCountDownIsEnding = (endDate?: string) => {
    return (
      <Countdown
        date={new Date(endDate ?? new Date()).getTime()}
        renderer={rendererIsEnding}
        onComplete={fetchData}
      />
    );
  };

  const renderDataSlider = () => {
    return (
      data?.filter((item: SliderProps) =>
        handleCheckTimeEndEvent(item.endNormalBuyAt)
      ) ?? []
    );
  };

  const handleSlideChange = (swiper: SwiperInstance): void => {
    setIndex(swiper.activeIndex);
    const currentImage = imageRefs.current?.[swiper.activeIndex];

    if (currentImage) {
      currentImage.style.transition = "transform 0.3s ease-in-out";
      currentImage.style.transform = "scale(1.05)";
      setTimeout(() => {
        currentImage.style.transform = "scale(1)";
      }, 300);
    }
  };

  const renderActionButton = (id: number, start: string, end: string) => {
    const now = moment();
    const startDate = moment(start);
    let cursor = "pointer";
    let background = "#653cd5";
    let isClickable = true;
    let btnText = "청약 신청하기 >";

    if (!startDate.isBefore(now)) {
      btnText = "청약 미리보기";
    }

    return (
      <div
        className="btn"
        onClick={
          isClickable
            ? () => {
                window.location.href = `${UrlInternal.GROUP_SALE_LIST}/${id}`;
              }
            : undefined
        }
        style={{
          cursor: cursor,
          background: background,
        }}
      >
        {btnText}
      </div>
    );
  };

  const getTextLength = (text: string) => {
    let length = 0;
    for (let char of text) {
      length += char.charCodeAt(0) > 127 ? 2 : 1;
    }
    return length;
  };

  const renderTitle = (title: string) => {
    const bigTitle = title.split(" (")[0];
    const smallTitle = "(" + title.split(" (")[1];
    let className = "";

    if (getTextLength(smallTitle) >= 36) {
      className = "small";
    }

    return (
      <div className="title">
        <div>{bigTitle}</div>
        <div className={className}>{smallTitle}</div>
      </div>
    );
  };

  const handleImageRightClick = (e: React.MouseEvent<HTMLImageElement>) => {
    e.preventDefault();
  };

  const renderImage = (
    thumbnail: string,
    startRegRandAt: string,
    endNormalBuyAt: string,
    artworkName: string,
    i: number
  ) => {
    if (!thumbnail) {
      return (
        <div className="left">
          <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No Image" />
        </div>
      );
    }

    let content = null;
    const isUpcoming = isCountDownForStartDate(startRegRandAt);
    const isClosingSoon = isCountDown(endNormalBuyAt);

    if (isUpcoming) {
      content = (
        <div className="countdown-salecoming">
          <div className="countdown-title">청약 오픈 예정</div>
          <div className="countdown-subtitle">
            {handleShowCountDownIsComming(startRegRandAt)}
          </div>
        </div>
      );
    } else if (isClosingSoon) {
      content = (
        <div className="count-num">
          {handleShowCountDownIsEnding(endNormalBuyAt)}
        </div>
      );
    }

    return (
      <div
        className="left"
        onClick={isMobile ? undefined : () => setIsPopup(thumbnail)}
        onContextMenu={handleImageRightClick}
      >
        <div className="img-wrapper">
          <img
            ref={(el) => {
              imageRefs.current[i] = el;
            }}
            className={content ? "count-img" : "img"}
            src={thumbnail}
            alt={artworkName || "art-img"}
          />
        </div>
        {content}
      </div>
    );
  };

  const calculatePercent = (v: SliderProps) => {
    return parseFloat(
      (((v?.v3TotalUnitReg * v?.unitPrice) / (v?.target * 0.9)) * 100).toFixed(
        1
      )
    );
  };

  const render80Bar = (v: SliderProps) => {
    const is80 =
      Math.round(
        ((v?.v3TotalUnitReg * v?.unitPrice) / (v?.target * 0.9)) * 100
      ) > 80;

    return (
      is80 && (
        <div className="bar80">
          <div className="bar-wrapper">
            <div className="title">
              <div className="left">
                누적 신청금액
                <span>
                  {moneyConvertLang(v?.v3TotalUnitReg * v?.unitPrice)}
                </span>
              </div>
              <div
                className="right"
                style={{
                  color: calculatePercent(v) >= 200 ? "#F80593" : "#653CD5",
                }}
              >
                {calculatePercent(v)}%
              </div>
            </div>
            <ProgressBar
              percent={calculatePercent(v)}
              isShining={true}
            ></ProgressBar>
          </div>
        </div>
      )
    );
  };

  return (
    <>
      {sliderData?.length > 0 && (
        <S.MainSlider target={target} isMobile={isMobile} is80Value={is80Value}>
          {isMobile ? (
            <S.Container className="container" target={target}>
              <Swiper
                onSwiper={(swiper) => {
                  swiperRef.current = swiper;
                }}
                {...mobileSettings}
                onSlideChange={handleSlideChange}
              >
                {sliderData?.map((v: any, i: any) => (
                  <SwiperSlide className="swiper-slide" key={i}>
                    <div className="text">
                      <div className="type">
                        {v?.isUpcomming ? "모집예정 청약" : "모집중인 청약"}
                      </div>
                      <div className="title">{renderTitle(v?.title)}</div>
                    </div>
                    {renderImage(
                      v?.thumbnail,
                      v?.startRegRandAt,
                      v?.endNormalBuyAt,
                      v?.artworkName,
                      i
                    )}
                    <div className="price">
                      <div className="item">
                        <div className="key">발행금액</div>
                        <div className="value">
                          {moneyConvertLang(v?.target)}
                        </div>
                      </div>
                      <div className="item">
                        <div className="key">모집금액</div>
                        <div className="value">
                          {moneyConvertLang(v?.target * 0.9)}
                        </div>
                      </div>
                      <div className="item">
                        <div className="key">주당금액</div>
                        <div className="value">
                          {moneyConvertLang(v?.unitPrice)}
                        </div>
                      </div>
                      <div className="item">
                        <div className="key">청약기간</div>
                        <div className="value">
                          {moment(v?.startDate).format("YY.MM.DD")}~
                          {moment(v?.endNormalBuyAt).format("MM.DD hh:mm A")}
                        </div>
                      </div>
                    </div>
                    {render80Bar(v)}
                    {renderActionButton(v?.id, v?.startDate, v?.endRegRandAt)}
                  </SwiperSlide>
                ))}
                <div className="swiper-pagination"></div>
              </Swiper>
            </S.Container>
          ) : (
            <S.Container className="container" target={target}>
              <Swiper
                className="swiper"
                {...settings}
                onSwiper={(swiper) => {
                  swiperRef.current = swiper;
                  setTimeout(() => setSwiperReady(true), 100);
                }}
                onSlideChange={handleSlideChange}
              >
                {sliderData?.map((v: any, i: any) => (
                  <SwiperSlide className="swiper-slide" key={i}>
                    <div className="content">
                      {renderImage(
                        v?.thumbnail,
                        v?.startRegRandAt,
                        v?.endNormalBuyAt,
                        v?.artworkName,
                        i
                      )}
                      <div className="right">
                        <div className="text">
                          <div className="type">
                            {v?.isUpcomming ? "모집예정 청약" : "모집중인 청약"}
                          </div>
                          {renderTitle(v?.title)}
                          <div className="description">
                            <div>
                              {v?.authorName}, {v?.artworkName}
                              {v?.artworkName && ","}
                            </div>
                            <div>
                              {`${v?.artSize ? v?.artSize?.trim() + "," : ""} ${
                                v?.artMaterial
                                  ? v?.artMaterial?.trim() +
                                    (v?.yearOfItem ? "," : "")
                                  : ""
                              } ${v?.yearOfItem?.trim()}`}
                            </div>
                          </div>
                        </div>
                        <div className="price">
                          <div className="item">
                            <div className="key">발행금액</div>
                            <div className="line">|</div>
                            <div className="value">
                              {moneyConvertLang(v?.target)}
                            </div>
                          </div>
                          <div className="item">
                            <div className="key">모집금액</div>
                            <div className="line">|</div>
                            <div className="value">
                              {moneyConvertLang(v?.target * 0.9)}
                            </div>
                          </div>
                          <div className="item">
                            <div className="key">청약기간</div>
                            <div className="line">|</div>
                            <div className="value">
                              {moment(v?.startDate).format("YYYY.MM.DD")} ~{" "}
                              {moment(v?.endNormalBuyAt).format(
                                "MM.DD hh:mm A"
                              )}
                            </div>
                          </div>
                          <div className="item">
                            <div className="key">주당금액</div>
                            <div className="line">|</div>
                            <div className="value">
                              {moneyConvertLang(v?.unitPrice)}
                            </div>
                          </div>
                        </div>
                        {renderActionButton(
                          v?.id,
                          v?.startDate,
                          v?.endRegRandAt
                        )}
                      </div>
                    </div>
                    {render80Bar(v)}
                  </SwiperSlide>
                ))}
                {index !== 0 && (
                  <div
                    className="swiper-button-prev prev"
                    onClick={() => swiperRef.current?.slidePrev()}
                  >
                    <img
                      src={SliderPrevButton}
                      width={20}
                      height={36}
                      alt="prev"
                    />
                  </div>
                )}
                {swiperReady && index !== sliderData.length - 1 && (
                  <div
                    className="swiper-button-next next"
                    onClick={() => {
                      const swiper = swiperRef.current;
                      if (!swiper) return;

                      const isLastSlide =
                        swiper.activeIndex >= sliderData.length - 1;
                      if (!isLastSlide) {
                        swiper.slideNext();
                      }
                    }}
                  >
                    <img src={SliderButton} width={20} height={36} alt="next" />
                  </div>
                )}
              </Swiper>
              {isPopup && (
                <div className="image-popup">
                  <ImagePopup
                    okEvent={() => setIsPopup("")}
                    visible={isPopup.length > 0}
                    content={
                      <span>
                        <img src={isPopup} alt="img"></img>
                      </span>
                    }
                  />
                </div>
              )}
            </S.Container>
          )}
        </S.MainSlider>
      )}
    </>
  );
};

export default MainSlider;
